import { useEffect, useMemo, useState } from 'react';
import Snow from './packs/Snow';
import Leaves from './packs/Leaves';
import Lights from './packs/Lights';
import Confetti from './packs/Confetti';
import Hearts from './packs/Hearts';
import Halloween from './packs/Halloween';
import SeasonalBorder from './SeasonalBorder';
import './overlays.css';

interface RemoteSettings {
  enabled?: boolean;
  pack?: string;
  intensity?: string;
  border?: string;
}

const enabledFromEnv =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_OVERLAYS_ENABLED) === 'true';

const packFromEnv =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_OVERLAY_PACK) || 'auto';

const intensityFromEnv =
  ((typeof import.meta !== 'undefined' && import.meta.env?.VITE_OVERLAY_INTENSITY) || 'med').toLowerCase();

function detectSeasonPack() {
  const m = new Date().getMonth(); // 0=Jan
  if (m >= 8 && m <= 10) return 'fall';     // Sep–Nov
  if (m === 11 || m <= 1) return 'winter';  // Dec–Feb
  return 'none';
}

function ls(key: string, fallback: string | null): string | null {
  try {
    const v = window.localStorage.getItem(key);
    return v ?? fallback;
  } catch {
    return fallback;
  }
}

export default function OverlayManager() {
  const [tick, setTick] = useState(0);
  const [remote, setRemote] = useState<RemoteSettings | null>(null);
  const [remoteLoaded, setRemoteLoaded] = useState(false);

  // Poll localStorage lightly so you can flip toggles without reload
  useEffect(() => {
    const i = setInterval(() => setTick((t) => t + 1), 1200);
    return () => clearInterval(i);
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const CORE_API = import.meta.env.VITE_CORE_API_URL || '';
        const res = await fetch(`${CORE_API}/api/v1/community/seasonal-settings`);
        if (!active) return;
        if (!res.ok) throw new Error('Failed to load overlay settings');
        const json = await res.json().catch(() => ({}));
        if (!active) return;
        setRemote({
          enabled: typeof json.enabled === 'boolean' ? json.enabled : undefined,
          pack: typeof json.pack === 'string' ? json.pack : undefined,
          intensity: typeof json.intensity === 'string' ? json.intensity : undefined,
          border: typeof json.border === 'string' ? json.border : undefined,
        });
      } catch (err) {
        if (!active) return;
        setRemote(null);
      } finally {
        if (active) setRemoteLoaded(true);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const overrideEnabled = useMemo(() => ls('overlay.enabled', null), [tick]);
  const overridePack = useMemo(() => ls('overlay.pack', null), [tick]);
  const overrideIntensity = useMemo(() => ls('overlay.intensity', null), [tick]);
  const overrideBorder = useMemo(() => ls('overlay.border', null), [tick]);

  const enabled = useMemo(() => {
    if (overrideEnabled === 'true') return true;
    if (overrideEnabled === 'false') return false;
    if (remoteLoaded && typeof remote?.enabled === 'boolean') return remote.enabled;
    return enabledFromEnv;
  }, [overrideEnabled, remoteLoaded, remote?.enabled]);

  const pack = useMemo(() => {
    const source = (overridePack || remote?.pack || packFromEnv || '').toLowerCase();
    return source === 'auto' ? detectSeasonPack() : source || 'none';
  }, [overridePack, remote?.pack, tick]);

  const intensity = useMemo(() => {
    return (overrideIntensity || remote?.intensity || intensityFromEnv || 'med').toLowerCase();
  }, [overrideIntensity, remote?.intensity, tick]);

  const border = useMemo(() => {
    return (overrideBorder || remote?.border || 'none').toLowerCase();
  }, [overrideBorder, remote?.border, tick]);

  const showOverlay = enabled && pack !== 'none';

  if (!showOverlay && border === 'none') {
    return null;
  }

  const props = { intensity };

  return (
    <>
      {showOverlay ? (
        pack === 'fall' ? (
          <Leaves {...props} />
        ) : pack === 'winter' ? (
          <Snow {...props} />
        ) : pack === 'lights' ? (
          <Lights {...props} />
        ) : pack === 'confetti' ? (
          <Confetti {...props} />
        ) : pack === 'hearts' ? (
          <Hearts {...props} />
        ) : pack === 'halloween' ? (
          <Halloween {...props} />
        ) : null
      ) : null}
      <SeasonalBorder variant={border} intensity={intensity} />
    </>
  );
}
